// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import type {
  AboutYouSectionContent,
  CashflowImpactSectionContent,
  NeedsObjectivesSectionContent,
  PensionRecommendationSectionContent,
  ProposalSection,
  RichTextSectionContent,
} from '../../../types/proposal';
import PlanAboutYouSection from './PlanAboutYouSection';
import PlanNeedsObjectivesSection from './PlanNeedsObjectivesSection';
import PlanRecommendationSection from './PlanRecommendationSection';
import PlanCashflowSection from './PlanCashflowSection';
import PlanRichTextSection from './PlanRichTextSection';
import './ProposalPlanView.css';

interface Props {
  readonly sections: ProposalSection[];
}

/** Dispatch one section to its typed renderer. Unknown keys fall back to the
 *  rich-text renderer when the content looks like `{blocks}`, else render
 *  nothing (forward-compatible with new section types). */
const renderSectionBody = (section: ProposalSection): React.ReactNode => {
  // The registry stores content as an opaque record; each renderer narrows
  // to its section's known shape (all fields are handled defensively).
  const content = (section.content ?? {}) as unknown;
  switch (section.sectionKey) {
    case 'ABOUT_YOU':
      return <PlanAboutYouSection content={content as AboutYouSectionContent} />;
    case 'NEEDS_OBJECTIVES':
      return <PlanNeedsObjectivesSection content={content as NeedsObjectivesSectionContent} />;
    case 'PENSION_RECOMMENDATION':
    case 'INVESTMENT_RECOMMENDATION':
    case 'PROTECTION_RECOMMENDATION':
      // Same content shape across all three quote-backed proposal types
      // (Investment & Protection Proposals PRD RQ-4) — one shared renderer.
      return (
        <PlanRecommendationSection content={content as PensionRecommendationSectionContent} />
      );
    case 'CASHFLOW_IMPACT':
      return <PlanCashflowSection content={content as CashflowImpactSectionContent} />;
    default:
      if (Array.isArray((content as RichTextSectionContent).blocks)) {
        return <PlanRichTextSection content={content as RichTextSectionContent} />;
      }
      return null;
  }
};

/**
 * Read-only rendering of a builder-born proposal's full plan — one card per
 * section in `sortOrder` — on the advisor proposal detail page. Mirrors the
 * client-ui `ProposalPlanView` (the shared frozen plan) with advisor-ui
 * chrome; for SENT+ proposals the sections carry the content frozen at share
 * time.
 */
export const ProposalPlanView: React.FC<Props> = ({ sections }) => {
  const ordered = [...sections].sort((a, b) => a.sortOrder - b.sortOrder);
  if (ordered.length === 0) return null;

  return (
    <div className="proposal-plan" data-testid="proposal-plan-view">
      {ordered.map((section) => (
        <section
          key={section.sectionKey}
          className="proposal-detail__card proposal-plan__section"
          data-testid={`proposal-plan-section-${section.sectionKey}`}
        >
          <h2 className="proposal-plan__section-title">{section.title}</h2>
          {renderSectionBody(section)}
        </section>
      ))}
    </div>
  );
};

export default ProposalPlanView;
